import { Button, cn, InfoPopover, Stack, Tooltip, TooltipContent, TooltipTrigger } from '@landler/component-library';
import { ComponentProps, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { MAP_LAYER_FACTS } from '@/api/rest/resources/types/fact';

import { Icon } from '../../a/Icon/Icon';
import { LEGEND_TYPE, LegendConfig } from './constants';
import { DEFAULT_LEGEND_FILTER, LegendFilter, SetLegendFilter } from './hooks/useLegendFilter';

type IndexLegendProps = {
  layerLabel: MAP_LAYER_FACTS;
};

type NumericLegendProps = IndexLegendProps & {
  legend: Extract<LegendConfig, { type: typeof LEGEND_TYPE.numeric }>;
};

export const IndexLegend = ({ layerLabel, legend }: NumericLegendProps) => {
  const { t } = useTranslation();

  return (
    <Stack className='rounded-2xl bg-white-100 p-4' spacing={2}>
      <LegendTitle layerLabel={layerLabel} />
      {legend?.unit && (
        <span className='typography-caption text-text-secondary'>
          {t('shared.projects.project.conservation.interactiveMap.legendUnit', { unit: legend.unit })}
        </span>
      )}

      <div className={`h-3 w-full rounded-2xl ${legend.pattern}`} />
      <Stack direction='row' className='justify-between' spacing={2}>
        {legend.values.map((value) => (
          <span key={value} className='typography-caption text-text-secondary'>
            {value}
          </span>
        ))}
      </Stack>
    </Stack>
  );
};

export const CategoryLegend = ({
  legend,
  layerLabel,
  setLegendFilter,
  legendFilter,
}: {
  layerLabel: MAP_LAYER_FACTS;
  legend: Extract<LegendConfig, { type: typeof LEGEND_TYPE.category }>;
  setLegendFilter: SetLegendFilter;
  legendFilter: LegendFilter;
}) => {
  if (!legend || !legend.values || legend.type !== LEGEND_TYPE.category) return null;

  const hasLegendFilters = legend.hasLegendFilter && legend?.filterKey;

  return (
    <LegendContainer layerLabel={layerLabel} legend={legend}>
      {legend.values.map((item) => (
        <div key={item.text}>
          {hasLegendFilters ? (
            <LegendItemFilterButton
              legend={legend}
              legendItem={item}
              setLegendFilter={setLegendFilter}
              legendFilter={legendFilter}
            />
          ) : (
            <LegendItem legendItem={item} />
          )}
        </div>
      ))}
    </LegendContainer>
  );
};

export const LegendContainer = ({
  children,
  layerLabel,
  legend,
  title,
}: { children: ReactNode; title?: string; layerLabel?: MAP_LAYER_FACTS } & {
  legend?: Extract<LegendConfig, { type: typeof LEGEND_TYPE.category }>;
}) => {
  return (
    <Stack className='rounded-2xl bg-white-100 p-4' spacing={2}>
      {layerLabel && <LegendTitle layerLabel={layerLabel} />}
      {title && <span className='typography-body2Semibold text-text-secondary'>{title}</span>}
      <Stack direction='row' spacing={2} className={cn('max-w-[220px] flex-wrap', legend?.className)}>
        {children}
      </Stack>
    </Stack>
  );
};

export const LegendItem = ({
  legendItem,
}: {
  legendItem: {
    text: string;
    color: string;
    tooltip?: string;
  };
}) => {
  const { t } = useTranslation();

  return (
    <Stack
      direction='row'
      key={legendItem.text}
      className='typography-caption text-text-secondary'
      centerMain
      spacing={2}
    >
      <div className='h-2 w-2 rounded-full' style={{ backgroundColor: legendItem.color }} />
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {t(legendItem.text as any)}
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {legendItem.tooltip && <InfoPopover body={t(legendItem.tooltip as any)} position='top' />}
    </Stack>
  );
};

const LegendTitle = ({ layerLabel }: { layerLabel: MAP_LAYER_FACTS }) => {
  const { t } = useTranslation();

  return (
    <Stack direction='row' spacing={2}>
      <span className='typography-body2Semibold text-text-secondary'>
        <span>
          {t(`shared.projects.project.heatMapLayers.${layerLabel}.title`)}
          <Tooltip>
            <TooltipTrigger className='items-center'>
              <Icon icon='ri-information-line' className='ml-1 text-active-54 typography-body1' />
            </TooltipTrigger>
            <TooltipContent className='whitespace-pre-wrap'>
              {t(`shared.projects.project.heatMapLayers.${layerLabel}.tooltip.body`)}
            </TooltipContent>
          </Tooltip>
        </span>
      </span>
    </Stack>
  );
};

export const Legend = ({ className, ...delegated }: ComponentProps<'div'>) => {
  return <div className={cn('absolute bottom-6 left-6', className)} {...delegated} />;
};

const LegendItemFilterButton = ({
  legend,
  legendItem,
  setLegendFilter,
  legendFilter,
}: {
  legend: Extract<LegendConfig, { type: typeof LEGEND_TYPE.category }>;
  legendItem: Pick<Extract<LegendConfig, { type: typeof LEGEND_TYPE.category }>, 'values'>['values'][number];
  setLegendFilter: SetLegendFilter;
  legendFilter: LegendFilter;
}) => {
  const { t } = useTranslation();

  if (!legendItem?.filterValue || !legend.filterKey) {
    throw new Error(
      `LegendItemFilterButton cannot have missing legend filterValue or legend filterKey: ${JSON.stringify({ legendItem, legend })}`,
    );
  }

  const isLegendFilterActive = legendFilter.value.includes(legendItem.filterValue);

  const handleClick = () => {
    const newFilters = isLegendFilterActive
      ? legendFilter.value.filter((f) => f !== legendItem.filterValue)
      : [...legendFilter.value, legendItem.filterValue];

    if (!newFilters || newFilters.length === 0) {
      setLegendFilter({
        ...DEFAULT_LEGEND_FILTER,
      });
      return;
    }

    const itemFilter = `${legend.filterKey} IN ('${newFilters.join("','")}')`;

    setLegendFilter({
      key: legend.filterKey as string,
      value: newFilters as string[],
      filter: itemFilter,
    });
  };

  const button = (
    <Button
      variant='outline'
      size='small'
      className={cn('py-1 px-2', isLegendFilterActive && 'border-primary-100 bg-primary-12')}
      onClick={handleClick}
    >
      <div className='h-2 w-2 rounded-full' style={{ backgroundColor: legendItem.color }} />
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {t(legendItem.text as any)}
    </Button>
  );

  return (
    <Stack
      direction='row'
      key={legendItem.text}
      className='typography-caption text-text-secondary'
      centerMain
      spacing={1}
    >
      {legendItem.tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <span>{t(legendItem.tooltip as any)}</span>
          </TooltipContent>
        </Tooltip>
      ) : (
        button
      )}
    </Stack>
  );
};
